<script setup lang="ts">
import { QItem, QItemSection, QTooltip } from 'quasar'
import { Color, ColorSemantic } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PLoader from '../loader/PLoader.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'

type Props = {
  text: string
  cy: string
  href?: string
  hrefTarget?: string
  iconStart?: string
  iconEnd?: string
  focused?: boolean
  loading?: boolean
  active?: boolean
  disable?: boolean
  tooltip?: null | string
  ellipsis?: boolean
  color?: Color | ColorSemantic
  iconStartColor?: null | Color | ColorSemantic
  iconEndColor?: null | Color | ColorSemantic
}

type Emits = {
  (e: 'click', evt: Event): void
}

const props = withDefaults(defineProps<Props>(), {
  focused: false,
  loading: false,
  active: false,
  disable: false,
  tooltip: null,
  ellipsis: false,
  color: ColorSemantic.TEXT_STANDARD,
  iconStartColor: null,
  iconEndColor: null,
})

const emits = defineEmits<Emits>()

const onClick = (e: Event): void => {
  if (!props.disable && !props.loading) {
    emits('click', e)
  }
}
</script>

<template>
  <q-item
    :focused="focused"
    :active="active"
    :data-cy="cy"
    :disable="disable"
    :href="href"
    :target="hrefTarget"
    clickable
    class="p-item p-flex p-items-center"
    @click="onClick"
  >
    <p-loader v-if="loading" :color="color" :size="24" />
    <slot name="iconStart">
      <p-icon
        v-if="!loading && iconStart"
        :icon="iconStart"
        :color="iconStartColor !== null ? iconStartColor : color"
      />
    </slot>

    <template v-if="($slots.iconStart || iconStart || loading) && (text || $slots.text)">
      <p-space :mobile="PSpaceSize.SM" />
    </template>

    <q-item-section>
      <slot name="text">
        <p-text-form-body :text="text" :color="color" :ellipsis="ellipsis" />
      </slot>
    </q-item-section>

    <template v-if="(text || $slots.text) && iconEnd">
      <p-space :mobile="PSpaceSize.SM" />
    </template>

    <template v-if="iconEnd">
      <p-icon :icon="iconEnd" :color="iconEndColor !== null ? iconEndColor : color" />
    </template>

    <slot />

    <q-tooltip
      v-if="tooltip"
      anchor="bottom middle"
      self="top middle"
      transition-duration="0"
      :offset="[0, 6]"
    >
      <p-text-description :text="tooltip" :color="Color.WHITE" />
    </q-tooltip>
  </q-item>
</template>
